/**
 * Async lookup loaders for the Buildings FilterBar.
 *
 * Accounts have a dedicated /lookup/accounts endpoint with text search and pagination.
 * Tags and additional territories use the generic /lookup endpoint.
 * Sales persons use /lookup?type=sales_person.
 * Territories are passed as static props and handled by createArrayLoader (see BuildingsListing.tsx).
 */
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import api from '@utils/axios';
import { loadAccountsByIds, loadLookupByIds } from '@helpers/lookupByIds';
const LOOKUP_PAGE_SIZE = 25;
/* eslint-enable @typescript-eslint/naming-convention, camelcase */
export function createAccountsLoader() {
    return (query, cursor) => __awaiter(this, void 0, void 0, function* () {
        const skip = cursor ? parseInt(cursor, 10) : 0;
        const params = {
            take: LOOKUP_PAGE_SIZE,
            skip
        };
        if (query.trim()) {
            params['filter[filters][0][value]'] = query.trim();
        }
        const response = yield api.get('/lookup/accounts', { params });
        // eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
        const { total_count: totalCount, data } = response.data;
        const options = data.map((a) => ({
            value: a.value,
            label: a.label
        }));
        const nextOffset = skip + LOOKUP_PAGE_SIZE;
        const hasMore = nextOffset < totalCount;
        return {
            options,
            nextCursor: hasMore ? String(nextOffset) : undefined
        };
    });
}
/* eslint-enable @typescript-eslint/naming-convention, camelcase */
function createLookupLoader(type) {
    return (query, cursor) => __awaiter(this, void 0, void 0, function* () {
        const skip = cursor ? parseInt(cursor, 10) : 0;
        const params = {
            type,
            take: LOOKUP_PAGE_SIZE,
            skip
        };
        if (query.trim()) {
            params['filter[filters][0][value]'] = query.trim();
            params['filter[filters][0][field]'] = 'label';
            params['filter[filters][0][operator]'] = 'contains';
            params['filter[filters][0][ignoreCase]'] = 'true';
            params['filter[logic]'] = 'and';
        }
        const response = yield api.get('/lookup', { params });
        // eslint-disable-next-line @typescript-eslint/naming-convention, camelcase
        const { total_count: totalCount, data } = response.data;
        const options = data.map((item) => {
            var _a, _b;
            return ({
                value: item.value,
                label: item.label,
                subtitle: (_b = (_a = item.address) !== null && _a !== void 0 ? _a : item.subLabel) !== null && _b !== void 0 ? _b : undefined
            });
        });
        const nextOffset = skip + LOOKUP_PAGE_SIZE;
        const hasMore = nextOffset < totalCount;
        return {
            options,
            nextCursor: hasMore ? String(nextOffset) : undefined
        };
    });
}
export function createTagsLoader() {
    return createLookupLoader('tags');
}
export function createAdditionalTerritoriesLoader() {
    return createLookupLoader('additional_territory');
}
export function createSalesPersonsLoader() {
    return createLookupLoader('sales_person');
}
export function createAccountsByIdsLoader() {
    return (ids) => loadAccountsByIds(ids);
}
export function createTagsByIdsLoader() {
    return (ids) => loadLookupByIds('tags', ids);
}
export function createAdditionalTerritoriesByIdsLoader() {
    return (ids) => loadLookupByIds('additional_territory', ids);
}
export function createSalesPersonsByIdsLoader() {
    return (ids) => loadLookupByIds('sales_person', ids);
}
